/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Typography, Autocomplete, TextField, Chip,
    Alert, Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Iconify from '../Iconify';
import DentallyAvailabilityStep from './DentallyAvailabilityStep';
import { fetchDentallyTreatments, fetchDentallyTreatmentFee } from '../../utils/dentallyApi';
import { callApi } from '../../utils/apiCall';
import { useSelector } from '../../redux/store';
import { grey } from '../../theme/palette';
import { sectionCardStyle, sectionLabelStyle } from '../../styles/DentallyBookingStyle';

/**
 * Combined Dentally treatment selector + availability picker for the Create Lead wizard.
 *
 * Props:
 *   practiceId              — required
 *   selectedSlot            — currently selected slot (controlled, from parent)
 *   onSlotSelected          — called with slot object or null
 *   selectedDentallyTreatment — currently selected Dentally treatment object (controlled, from parent)
 *   onTreatmentSelected     — (dentallyTreatmentObj, crmTreatmentName | null) => void
 *   assignees               — controlled array of booked-with practice user objects
 *   onAssigneesChanged      — called when booked-with changes
 */
export default function DentallyTreatmentAndAvailability({
    practiceId,
    selectedSlot,
    onSlotSelected,
    selectedDentallyTreatment,
    onTreatmentSelected,
    assignees,
    onAssigneesChanged,
    availSearchState,
    onAvailSearchStateChange,
}) {
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';
    const { practiceUser } = useSelector((state) => state.practiceUser);
    const { practice: practiceData } = useSelector((state) => state.practice);

    const [dentallyTreatments, setDentallyTreatments] = useState([]);
    const [crmTreatments, setCrmTreatments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [assignToState, setAssignToState] = useState(assignees || []);
    const [selectedCrmName, setSelectedCrmName] = useState('');
    const latestTreatmentRef = useRef(null);

    // Sync external assignees prop
    useEffect(() => {
        if (assignees) setAssignToState(assignees);
    }, [assignees]);

    useEffect(() => {
        if (!practiceId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);

        Promise.all([
            fetchDentallyTreatments(practiceId, { activeOnly: true }).catch(() => []),
            callApi(
                `/api/v1/practices/${practiceId}/treatments/?enabled=true&ordering=-is_starred,treatment_item__name&limit=500`
            ).then((r) => (r.ok ? r.data?.results || [] : [])).catch(() => []),
        ]).then(([dtList, crmList]) => {
            if (cancelled) return;
            setDentallyTreatments(dtList);
            setCrmTreatments(
                crmList
                    .filter((t) => t.dentally_treatment_id && String(t.dentally_treatment_id).trim())
                    .map((t) => ({
                        id: t.id,
                        name: t.treatment_item?.name || '',
                        dentally_treatment_id: String(t.dentally_treatment_id || ''),
                        dentally_appointment_reason: t.dentally_appointment_reason || null,
                        is_starred: t.is_starred || false,
                        price: t.price || 0,
                        custom_price: t.custom_price || 0,
                        treatment_item: t.treatment_item || null,
                    }))
            );
        }).catch(() => {
            if (cancelled) return;
            setError('Failed to load treatments. Please try again.');
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });

        // eslint-disable-next-line consistent-return
        return () => { cancelled = true; };
    }, [practiceId]);

    // Only show synced treatments (have a Dentally link) in starred chips
    const starredTreatments = useMemo(
        () => crmTreatments.filter((t) => t.is_starred && t.dentally_treatment_id).slice(0, 5),
        [crmTreatments]
    );

    // DEV-only: auto-select "New Patient Exam" treatment when list loads (Test Practice only)
    const isTestPractice = practiceData?.name?.toLowerCase().includes('test practice');
    useEffect(() => {
        if (!import.meta.env.DEV || !isTestPractice || selectedDentallyTreatment || !crmTreatments.length || !dentallyTreatments.length) return;
        const examTreatment = crmTreatments.find((t) => t.name?.toLowerCase().includes('new patient exam'));
        if (examTreatment) {
            const treatmentKey = examTreatment.dentally_treatment_id || examTreatment.id;
            latestTreatmentRef.current = treatmentKey;
            setSelectedCrmName(examTreatment.name);
            const dentallyMatch = dentallyTreatments.find((dt) => String(dt.id) === examTreatment.dentally_treatment_id) || null;
            const crmPrice = examTreatment.custom_price || examTreatment.price || 0;
            const reason = examTreatment.dentally_appointment_reason || null;
            onTreatmentSelected(dentallyMatch || examTreatment, examTreatment.name, crmPrice, reason);
            // Fetch Dentally fee and prefer it
            if (examTreatment.dentally_treatment_id) {
                fetchDentallyTreatmentFee(practiceId, examTreatment.dentally_treatment_id)
                    .then((dentallyPrice) => { if (dentallyPrice > 0 && latestTreatmentRef.current === treatmentKey) onTreatmentSelected(dentallyMatch || examTreatment, examTreatment.name, dentallyPrice, reason); })
                    .catch(() => {});
            }
        }
    }, [crmTreatments, dentallyTreatments, isTestPractice]);

    const findCrmMatch = useCallback(
        (dentallyId) =>
            crmTreatments.find((c) => c.dentally_treatment_id === String(dentallyId)) || null,
        [crmTreatments]
    );

    const findDentallyMatch = useCallback(
        (dentallyTreatmentId) => {
            if (!dentallyTreatmentId) return null;
            return dentallyTreatments.find((dt) => String(dt.id) === dentallyTreatmentId) || null;
        },
        [dentallyTreatments]
    );

    const handleChipClick = useCallback((crmTreatment) => {
        const treatmentKey = crmTreatment.dentally_treatment_id || crmTreatment.id;
        latestTreatmentRef.current = treatmentKey;
        setSelectedCrmName(crmTreatment.name);
        const dentallyMatch = findDentallyMatch(crmTreatment.dentally_treatment_id);
        const crmPrice = crmTreatment.custom_price || crmTreatment.price || 0;
        const reason = crmTreatment.dentally_appointment_reason || null;
        onTreatmentSelected(dentallyMatch || crmTreatment, crmTreatment.name, crmPrice, reason);
        // Fetch Dentally fee and override if available
        if (crmTreatment.dentally_treatment_id) {
            fetchDentallyTreatmentFee(practiceId, crmTreatment.dentally_treatment_id)
                .then((dentallyPrice) => { if (dentallyPrice > 0 && latestTreatmentRef.current === treatmentKey) onTreatmentSelected(dentallyMatch || crmTreatment, crmTreatment.name, dentallyPrice, reason); })
                .catch(() => {});
        }
    }, [findDentallyMatch, onTreatmentSelected, practiceId]);

    const handleDentallySelect = useCallback((dentallyTreatment) => {
        if (!dentallyTreatment) {
            latestTreatmentRef.current = null;
            setSelectedCrmName('');
            onTreatmentSelected(null, null, 0, null);
            return;
        }
        const treatmentKey = String(dentallyTreatment.id);
        latestTreatmentRef.current = treatmentKey;
        const crmMatch = findCrmMatch(dentallyTreatment.id);
        setSelectedCrmName(crmMatch?.name || '');
        const crmPrice = crmMatch ? (crmMatch.custom_price || crmMatch.price || 0) : 0;
        const reason = crmMatch?.dentally_appointment_reason || null;
        onTreatmentSelected(dentallyTreatment, crmMatch ? crmMatch.name : null, crmPrice, reason);
        // Fetch Dentally fee and override if available
        fetchDentallyTreatmentFee(practiceId, dentallyTreatment.id)
            .then((dentallyPrice) => { if (dentallyPrice > 0 && latestTreatmentRef.current === treatmentKey) onTreatmentSelected(dentallyTreatment, crmMatch ? crmMatch.name : null, dentallyPrice, reason); })
            .catch(() => {});
    }, [findCrmMatch, onTreatmentSelected, practiceId]);

    const handleClearTreatment = useCallback(() => {
        setSelectedCrmName('');
        onTreatmentSelected(null, null, 0, null);
    }, [onTreatmentSelected]);

    const handleSetAssign = useCallback((values) => {
        setAssignToState(values);
        onAssigneesChanged?.(values);
    }, [onAssigneesChanged]);

    // Auto-fill booked-with from selected slot's practitioner
    useEffect(() => {
        if (!selectedSlot || selectedSlot._manual) return;
        const practName = selectedSlot._queried_practitioner_name;
        if (!practName || !practiceUser?.results?.length) return;

        const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, ' ').trim().split(/\s+/);
        const dentallyWords = normalize(practName);

        let bestMatch = null;
        let bestScore = 0;
        practiceUser.results.forEach((pu) => {
            const u = pu.user || {};
            const crmName = [u.salutation, u.first_name, u.last_name].filter(Boolean).join(' ');
            const crmWords = normalize(crmName);
            const score = dentallyWords.filter((w) => crmWords.some((cw) => cw.includes(w) || w.includes(cw))).length;
            if (score > bestScore) { bestScore = score; bestMatch = pu; }
        });

        if (bestMatch && bestScore > 0) {
            setAssignToState([bestMatch]);
            onAssigneesChanged?.([bestMatch]);
        }
    }, [selectedSlot]);

    const crmMatch = selectedDentallyTreatment ? findCrmMatch(selectedDentallyTreatment.id) : null;
    const displayName = selectedCrmName
        || crmMatch?.name
        || selectedDentallyTreatment?.nomenclature
        || selectedDentallyTreatment?.name
        || '';

    return (
        <Box sx={{ px: 3, py: 2 }}>
            {/* ── Treatment ──────────────────────────────────────────────── */}
            <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: '#00AB55', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700 }}>1</Box>
                <Iconify icon='mdi:tooth-outline' width={16} sx={{ color: '#637381' }} />
                <Typography sx={{ fontSize: '11px', color: '#637381', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Treatment</Typography>
            </Box>
            <Box sx={sectionCardStyle}>

            {error && <Alert severity='error' sx={{ mb: 1.5 }}>{error}</Alert>}

            {!selectedDentallyTreatment ? (
                <Box>
                    {/* Starred CRM treatments as quick-select chips */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                        {starredTreatments.map((t) => {
                            let textColor = grey[400];
                            if (isLight) textColor = grey[800];
                            return (
                                <Button
                                    key={t.id}
                                    variant='outlined'
                                    onClick={() => handleChipClick(t)}
                                    startIcon={
                                        <Iconify
                                            icon='material-symbols:circle'
                                            width={20}
                                            height={20}
                                            sx={{ color: grey[400] }}
                                        />
                                    }
                                    sx={{
                                        borderRadius: '50px',
                                        border: '1px solid rgba(145, 158, 171, 0.32)',
                                        m: '0 !important',
                                        p: '4px 12px 4px 5px',
                                        height: '32px',
                                        color: textColor,
                                        bgcolor: 'transparent',
                                        fontWeight: 400,
                                        textTransform: 'none',
                                        fontSize: '0.875rem',
                                        minWidth: 'auto',
                                        '&:hover': {
                                            bgcolor: 'rgba(145, 158, 171, 0.08)',
                                            borderColor: 'rgba(145, 158, 171, 0.48)',
                                        },
                                    }}
                                >
                                    {t.name}
                                    {t.dentally_treatment_id && t.dentally_treatment_id !== '' && (
                                        <Iconify icon='mdi:link-variant' width={14} sx={{ ml: 0.5, color: '#00AB55' }} />
                                    )}
                                    {t.dentally_appointment_reason && t.dentally_appointment_reason.trim() && (
                                        <Iconify icon='mdi:calendar-clock-outline' width={14} sx={{ ml: 0.5, color: '#855CF8' }} />
                                    )}
                                </Button>
                            );
                        })}
                        {starredTreatments.length === 0 && !loading && (
                            <Typography variant='caption' sx={{ color: 'text.disabled', ml: 0.5 }}>
                                Pin your favourite treatments in Settings to see them here
                            </Typography>
                        )}
                    </Box>

                    {/* Full Dentally treatment search */}
                    <Autocomplete
                        options={dentallyTreatments}
                        getOptionLabel={(t) => t.nomenclature || t.name || ''}
                        onChange={(_, val) => handleDentallySelect(val)}
                        loading={loading}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        renderOption={(props, t) => {
                            const crmName = findCrmMatch(t.id)?.name;
                            const reason = findCrmMatch(t.id)?.dentally_appointment_reason;
                            return (
                                <Box component='li' {...props} key={t.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant='body2'>{t.nomenclature || t.name}</Typography>
                                            {crmName && (
                                                <Typography variant='caption' color='text.secondary' display='block' sx={{ lineHeight: 1.3 }}>
                                                    → {crmName}
                                                </Typography>
                                            )}
                                        </Box>
                                        {crmName && <Chip size='small' label='CRM' color='success' variant='outlined' sx={{ fontSize: 10, height: 20 }} />}
                                        {reason && <Chip size='small' label={reason} color='info' variant='outlined' sx={{ fontSize: 10, height: 20, maxWidth: 100 }} />}
                                    </Box>
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size='small'
                                placeholder='Search all Dentally treatments…'
                            />
                        )}
                        size='small'
                        sx={{ mb: 1 }}
                    />
                </Box>
            ) : (
                /* Selected treatment info bar */
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        mb: 1.5,
                        alignItems: 'center',
                        p: 1.5,
                        width: '100%',
                        bgcolor: 'rgba(145, 158, 171, 0.04)',
                        borderRadius: '12px',
                        border: '1px solid rgba(145, 158, 171, 0.16)',
                    }}
                >
                    <Box sx={{ flex: '1 1 auto', minWidth: '120px' }}>
                        <Typography
                            sx={{
                                fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary',
                                mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px',
                            }}
                        >
                            Treatment
                        </Typography>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
                            {displayName}
                        </Typography>
                    </Box>
                    {crmMatch ? (
                        <Chip
                            size='small'
                            color='success'
                            icon={<Iconify icon='mdi:link-variant' width={14} />}
                            label={`CRM: ${crmMatch.name}`}
                        />
                    ) : (
                        <Chip
                            size='small'
                            color='warning'
                            icon={<Iconify icon='mdi:alert-outline' width={14} />}
                            label='No CRM mapping'
                        />
                    )}
                    <Button
                        variant='text'
                        size='small'
                        onClick={handleClearTreatment}
                        startIcon={<Iconify icon='mdi:pencil-outline' width={16} />}
                        sx={{
                            textTransform: 'none', fontWeight: 500, fontSize: '0.8125rem',
                            color: '#00A76F', px: 1.5, py: 0.75, borderRadius: '8px',
                            '&:hover': { bgcolor: 'rgba(0, 167, 111, 0.08)' },
                        }}
                    >
                        Change
                    </Button>
                </Box>
            )}

            </Box>
            </Box>

            {/* ── Availability ───────────────────────────────────────────── */}
            <Box sx={sectionCardStyle}>
            <Typography sx={sectionLabelStyle}>
                <Iconify icon='mdi:calendar-clock' width={15} sx={{ color: '#00AB55' }} />
                Availability
            </Typography>
            <DentallyAvailabilityStep
                practiceId={practiceId}
                selectedSlot={selectedSlot}
                onSelect={onSlotSelected}
                assignToState={assignToState}
                onAssignToChange={handleSetAssign}
                availSearchState={availSearchState}
                onAvailSearchStateChange={onAvailSearchStateChange}
            />
            </Box>
        </Box>
    );
}

DentallyTreatmentAndAvailability.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    selectedSlot: PropTypes.object,
    onSlotSelected: PropTypes.func.isRequired,
    selectedDentallyTreatment: PropTypes.object,
    onTreatmentSelected: PropTypes.func.isRequired,
    assignees: PropTypes.array,
    onAssigneesChanged: PropTypes.func,
    availSearchState: PropTypes.object,
    onAvailSearchStateChange: PropTypes.func,
};

DentallyTreatmentAndAvailability.defaultProps = {
    selectedSlot: null,
    selectedDentallyTreatment: null,
    assignees: [],
    onAssigneesChanged: null,
    availSearchState: null,
    onAvailSearchStateChange: null,
};
